import type { Primitive } from '../types';
import type { SeverityLevel } from '@sentry/nuxt';

import { captureException, captureMessage, withScope } from '@sentry/nuxt';
import { CombinedGraphQLErrors } from '@apollo/client';
import { isErrorLike } from '@apollo/client/errors';

/** Send a message to Sentry, this handles extra info and parsing. */
export async function captureMessageForSentry(
	name: string,
	payload: { [key: string]: any; error?: Error } = {},
	level: SeverityLevel = 'info',
	tags?: { [key: string]: Primitive }
) {
	if ((process.env.NODE_ENV === 'development' || JW_CONFIG.DOMAIN === 'moviecycle.com') && payload.error) {
		console.error(`${name}\n`, `${payload.where ?? ''}\n`, payload.error);
	}

	if (import.meta.server && payload.error) {
		captureException(payload.error);
		console.error({ sentryError: payload.error, message: `${name}: ${payload.where ?? ''}` });
		return;
	}

	// Check if Sentry is initialized
	// if (sentryInitMachine?.CURRENT() !== INIT_STEPS.INITIALIZED) return false;

	withScope(scope => {
		const error = payload['error'];
		const asException = level === 'error' && error;
		if (asException) {
			delete payload['error'];
		}

		scope.setExtra('1. version', BUILD_CONFIG.appVersion);
		scope.setExtra('2. message', payload);

		if (isErrorLike(error)) {
			if (CombinedGraphQLErrors.is(error)) {
				if (error.errors.length) {
					scope.setExtra(
						'3. graphQLErrors',
						error.errors.map(e => ({
							message: e.message,
							path: JSON.stringify(e.path),
							locations: JSON.stringify(e.locations),
						}))
					);
				}
			} else {
				scope.setExtra('4. networkError', {
					name: error.name,
					message: error.message,
					cause: error.cause ?? 'unknown',
				});
			}
		}

		scope.setLevel(level);

		// set additional tags
		if (tags) scope.setTags(tags);

		if (asException) {
			if (error.message && Object.getOwnPropertyDescriptor(error, 'message')?.set != null) {
				error.message = [name, error!.message].join(' ');
			}
			captureException(error);
		} else {
			captureMessage(name);
		}
	});
}
